import { Radio, Stack } from '@mui/joy';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
import { Form, FormField } from 'core/components';
import { ApiStatus, InputType, NotificationPosition } from 'core/enums';
import { useFocusInputFunctionality } from 'core/hooks';
import { useFormContext, useNotificationContext } from 'core/states';
import { AdvancedPartNumberFormField } from 'parts/elements';
import { IPart } from 'parts/types';
import { PricingProfilePartNumberType } from 'pricing/enums';
import { PricingHelper } from 'pricing/helpers';
import { partNumberService } from 'pricing/services';
import {
  IPricingProfilePartNumber,
  IPricingProfilePartNumberForm,
  IPricingProfilePartPricing,
} from 'pricing/types';
import { Direction } from 'theme/enums';

import {
  deriveFixedPrice,
  deriveMarginPercentage,
  getCostBasis,
  getUomPricing,
  MARGIN_DECIMALS,
} from './helpers';
import { RemovePricingProfilePartNumbersButton } from './RemovePricingProfilePartNumbersButton';
import { UpsertPricingProfilePartNumberCostFields } from './UpsertPricingProfilePartNumberCostFields';

interface IProps {
  pricingProfilePartNumber?: IPricingProfilePartNumber;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    reset?: UseFormReset<IPricingProfilePartNumberForm>
  ) => (data: IPricingProfilePartNumberForm) => void;
  onRemoveSuccess?: () => void;
}

export const UpsertPricingProfilePartNumberFormFields = ({
  pricingProfilePartNumber,
  isOpen,
  onClose,
  onSubmit,
  onRemoveSuccess,
}: IProps) => {
  const [uomOptions, setUomOptions] = useState<string[]>([]);
  const [partPricing, setPartPricing] = useState<Record<string, IPricingProfilePartPricing>>(null);
  const [standardCostMultiplier, setStandardCostMultiplier] = useState<number>(null);

  const { t } = useTranslation();

  const { watch, setValue, control, reset } = useFormContext();
  const { clearNotification, showNotification } = useNotificationContext();
  const { setFocusElementName } = useFocusInputFunctionality();
  const { user } = useAuthUser();

  const isAlternateVersionEnabled = Boolean(user?.alternateVersionEnabled);

  const typeValue = watch('type');
  const uomValue = watch('uom');
  const priceValue = watch('price');
  const marginValue = watch('margin');
  const costValue = watch('cost');

  const companyPricing = useMemo(
    () => getUomPricing(partPricing, uomValue),
    [partPricing, uomValue]
  );

  const costBasis = getCostBasis(costValue, companyPricing?.cost);

  const validatePart = useCallback(
    async (input?: { number: string; manufacturerId: number }) => {
      setUomOptions([]);
      setPartPricing(null);
      setStandardCostMultiplier(null);

      if (!input) {
        return;
      }

      showNotification({
        title: t('titleStatus'),
        message: t('messagePartValidationInProgress'),
        type: ApiStatus.LOADING,
        position: NotificationPosition.FORM,
      });

      const { record } = await partNumberService.validatePartNumber(
        input.number,
        input.manufacturerId
      );

      if (record?.success) {
        setUomOptions(record.uoms);
        setPartPricing(record.pricing);
        setStandardCostMultiplier(record.pricingMultipliers?.standardCost);

        if (record.uoms.length === 1) {
          setValue('uom', record.uoms[0]);
        }

        clearNotification();
        setFocusElementName('price');
      }
    },
    [clearNotification, setFocusElementName, setValue, showNotification, t]
  );

  useEffect(() => {
    if (!isOpen) {
      reset();

      return;
    }

    if (!pricingProfilePartNumber) {
      setFocusElementName('part');
      validatePart();

      return;
    }

    validatePart({
      number: pricingProfilePartNumber.part,
      manufacturerId: pricingProfilePartNumber.manufacturerId,
    });
  }, [isOpen, pricingProfilePartNumber, reset, setFocusElementName, validatePart]);

  // The unselected half of the fixed price/margin pair shows the value derived from the cost basis.
  // Each effect watches the field it writes and bails once it already holds the derived value, so
  // the write settles in one pass and re-applies if the form is reset underneath it.
  useEffect(() => {
    if (PricingProfilePartNumberType.PRICE !== typeValue) {
      return;
    }

    const derivedMargin = deriveMarginPercentage(costBasis, priceValue, MARGIN_DECIMALS) ?? '';

    if (marginValue === derivedMargin) {
      return;
    }

    setValue('margin', derivedMargin);
  }, [costBasis, marginValue, priceValue, setValue, typeValue]);

  useEffect(() => {
    if (PricingProfilePartNumberType.MARGIN !== typeValue) {
      return;
    }

    const derivedPrice =
      deriveFixedPrice(costBasis, marginValue, PricingHelper.DEFAULT_PRICING_DECIMALS) ?? '';

    if (priceValue === derivedPrice) {
      return;
    }

    setValue('price', derivedPrice);
  }, [costBasis, marginValue, priceValue, setValue, typeValue]);

  const onChangePartNumber = useCallback(
    (part: IPart | null) => {
      if (!part) {
        setUomOptions([]);
        setPartPricing(null);
        setStandardCostMultiplier(null);
        setValue('uom', null);

        return;
      }

      validatePart({ number: part.number, manufacturerId: part.manufacturerId });
    },
    [setValue, validatePart]
  );

  const onUomChange = (_, uom: string) => {
    if (pricingProfilePartNumber?.uom && uom !== pricingProfilePartNumber?.uom) {
      showNotification({
        title: t('titleWarning'),
        message: t('messagePricingProfilePartNumberUomChanged', {
          from: pricingProfilePartNumber?.uom,
          to: uom,
        }),
        type: ApiStatus.INFO,
        position: NotificationPosition.FORM,
        withoutCloseButton: true,
      });
    } else {
      clearNotification();
    }
  };

  const title = useMemo(() => {
    return pricingProfilePartNumber
      ? t('titleUpdatePricingProfilePartNumber')
      : t('titleAddPricingProfilePartNumber');
  }, [pricingProfilePartNumber, t]);

  const removeButton = useMemo(() => {
    return pricingProfilePartNumber ? (
      <RemovePricingProfilePartNumbersButton
        pricingProfilePartNumbers={[pricingProfilePartNumber]}
        buttonLabel={t('buttonLabelRemovePricingProfilePartNumber')}
        onRemoveSuccess={onRemoveSuccess}
        notificationPosition={NotificationPosition.FORM}
      />
    ) : null;
  }, [pricingProfilePartNumber, onRemoveSuccess, t]);

  return (
    <Form
      title={title}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={onSubmit}
      extraButtons={removeButton}
    >
      <AdvancedPartNumberFormField
        onChangePartNumber={onChangePartNumber}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
        disabled={!!pricingProfilePartNumber}
        hasCreateOption={false}
        level={1}
      />

      <FormField
        key={uomValue}
        name="uom"
        type={InputType.SELECT}
        inputProps={{
          label: t('labelUom'),
          placeholder: t('placeholderUpsertLineUom'),
          onChange: onUomChange,
          getLabel: (option) => option,
          getValue: (option) => option,
          options: uomOptions,
          disabled: uomOptions.length === 0,
        }}
      />

      {!isAlternateVersionEnabled && (
        <UpsertPricingProfilePartNumberCostFields
          companyPricing={companyPricing}
          standardCostMultiplier={standardCostMultiplier}
        />
      )}

      <FormField
        name="type"
        type={InputType.RADIO_GROUP}
        inputProps={{
          onChange: () => {
            setValue('price', '');
            setValue('margin', '');
          },
          renderRadio: (choice, onChange) => {
            return (
              <Stack
                direction={Direction.ROW}
                sx={{ gap: 1, alignItems: 'center' }}
                key={choice.value}
              >
                <Radio
                  value={choice.value}
                  label={choice.label}
                  sx={{ minWidth: '150px' }}
                  onChange={onChange}
                />
                <FormField
                  name={`${choice.value}`}
                  type={InputType.NUMBER_INPUT}
                  inputProps={{
                    control,
                    maxWidth: '150px',
                    slotProps: choice.slotProps,
                    disabled: typeValue !== choice.value,
                  }}
                />
              </Stack>
            );
          },
          choices: [
            {
              label: t('labelFixedPrice'),
              value: PricingProfilePartNumberType.PRICE,
              slotProps: {
                input: {
                  thousandSeparator: true,
                  prefix: '$',
                },
              },
            },
            {
              label: t('labelSpecificMargin'),
              value: PricingProfilePartNumberType.MARGIN,
              slotProps: {
                input: {
                  max: 100,
                  decimalScale: MARGIN_DECIMALS,
                  suffix: '%',
                },
              },
            },
          ],
        }}
      />
    </Form>
  );
};
